import { BillStatus } from "@redotech/cockroachdb-codegen/tables/billing/bill-status";
import { SubscriptionProduct } from "@redotech/redo-billing-common/unified-billing/enums";
import { BillingTab, getBillingPageLink } from "../routing/links";
import {
  BILL_DATE_PARAM,
  lastMonthsSelection,
  serializeBillDateSelection,
} from "./bill-date-selection";
import { BILL_FILTERS_PARAM, encodeBillFilters } from "./bill-filter-params";

/** The Bills page's top-level tab strip, keyed off the same param it writes. */
export const BILLS_TAB_PARAM = "tab";
export const BILLS_PAYMENT_METHODS_TAB = "payment_methods";

// Bills defaults to a single month, too narrow an answer to "what have I been
// charged for this product".
const PRODUCT_ENTRY_MONTHS = 6;

// A decline can sit unpaid a while before the merchant reads the banner, so
// reach back further — an empty table is the dead end this link exists to avoid.
const FAILED_ENTRY_MONTHS = 12;

function billsLink(
  teamId: string,
  now: Date,
  months: number,
  filters: { products?: SubscriptionProduct[]; statuses?: BillStatus[] },
): string {
  const params = new URLSearchParams({
    [BILL_DATE_PARAM]: serializeBillDateSelection(
      lastMonthsSelection(now, months),
    ),
  });
  const encoded = encodeBillFilters(filters);
  if (encoded) {
    params.set(BILL_FILTERS_PARAM, encoded);
  }
  return `${getBillingPageLink(teamId, BillingTab.BILLS)}?${params.toString()}`;
}

/** Bills, scoped to one product's invoices — a product billing page's entry. */
export function productBillsLink(
  teamId: string,
  products: SubscriptionProduct[],
  now: Date,
): string {
  return billsLink(teamId, now, PRODUCT_ENTRY_MONTHS, { products });
}

/**
 * Bills, scoped to the invoices that actually failed to collect — the bill the
 * failed-payment banner is complaining about.
 */
export function failedBillsLink(teamId: string, now: Date): string {
  return billsLink(teamId, now, FAILED_ENTRY_MONTHS, {
    statuses: [BillStatus.Failed],
  });
}

/** Where "update your billing info" goes: the payment methods tab. */
export function paymentMethodsLink(teamId: string): string {
  return `${getBillingPageLink(teamId, BillingTab.BILLS)}?${BILLS_TAB_PARAM}=${BILLS_PAYMENT_METHODS_TAB}`;
}
